import {
  asCurrency,
  getAmortization,
  getMonthlyPayment,
  getNumPayments,
  updateValuesFromFields,
  validateFields,
} from '../util'
import { useAppState } from '../Context'
import { EquityBalanceInterestChart } from '../components/Charts'
import { AmortizationScheduleTable } from '../components/Tables'
import { InputFields } from '../components/Fields'
import CalculateResults from '../components/CalculateResults'
import CalculateRow from '../components/CalculateRow'
import currency from 'currency.js'

function PanelInput() {
  return <InputFields />
}

function PanelCalculate() {
  const { values } = useAppState()

  const pointsCost = currency(values.remainingBalance).multiply(values.points / 100)
  const originationCosts = currency(values.feesCosts).add(pointsCost)

  const totalOriginationCosts = currency(0).subtract(originationCosts)

  const totalTaxedInterest = values.amortization.totalInterest.multiply(values.taxRate / 100)
  const totalLoanCost = values.amortization.totalPayment.subtract(totalTaxedInterest)

  const pointsAmount = currency(values.remainingBalance).multiply(values.points / 10000)

  const totalTaxedInterestRefinance = values.amortizationRefinance.totalInterest.multiply(values.taxRate / 100)
  const totalLoanCostRefinance = values.amortizationRefinance.totalPayment
    .subtract(totalTaxedInterestRefinance)
    .subtract(pointsAmount)
    .add(originationCosts)

  const totalSavings = totalLoanCost.subtract(totalLoanCostRefinance)

  return (
    <CalculateResults>
      <table className="w-full text-xs md:text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th></th>
            <th className="py-3 px-2 text-right">Current Loan</th>
            <th className="py-3 px-2 text-right">Refinance</th>
            <th className="py-3 px-2 text-right">Savings</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-300">
          <tr>
            <td className="py-3 px-2">Monthly Payment:</td>
            <td className="py-3 px-2 text-right">{asCurrency(values.amortization.monthlyPayment)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(values.amortizationRefinance.monthlyPayment)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(values.monthlySavings)}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">Origination Cost:</td>
            <td className="py-3 px-2 text-right">{asCurrency(0)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(originationCosts)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(totalOriginationCosts)}</td>
          </tr>
          <tr>
            <td className="py-3 px-2">Loan Term (Months):</td>
            <td className="py-3 px-2 text-right">{values.amortization.numPayments}</td>
            <td className="py-3 px-2 text-right">{values.amortizationRefinance.numPayments}</td>
            <td></td>
          </tr>
          <tr>
            <td className="py-3 px-2">
              Total Loan Cost<sup>*</sup>:
            </td>
            <td className="py-3 px-2 text-right">{asCurrency(totalLoanCost)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(totalLoanCostRefinance)}</td>
            <td className="py-3 px-2 text-right">{asCurrency(totalSavings)}</td>
          </tr>
        </tbody>
      </table>

      <CalculateRow className="p-4">
        <div>
          <strong>* Note:</strong> The total loan cost figure is actually the present value of all the payments for each
          loan, using the investment rate of return as a discount rate.
        </div>
      </CalculateRow>
    </CalculateResults>
  )
}

function PanelChart() {
  const { values } = useAppState()

  return (
    <div className="py-4 space-y-4">
      <EquityBalanceInterestChart amortization={values.amortization} title="Original Loan" />
      <EquityBalanceInterestChart amortization={values.amortizationRefinance} title="New Loan" />
    </div>
  )
}

function PanelDetail() {
  const { values } = useAppState()

  return (
    <>
      <div className="p-3 font-bold">Current Payment Schedule</div>
      <AmortizationScheduleTable amortization={values.amortization} />
      <div className="p-3 font-bold">Refinancing Payment Schedule</div>
      <AmortizationScheduleTable amortization={values.amortizationRefinance} />
    </>
  )
}

export default {
  name: 'Is refinancing my loan a good idea?',
  tabs: [
    {
      name: 'Input',
      component: PanelInput,
      beforeTabChange: (dispatch, fields, values) => {
        const validate = validateFields(fields)

        if (validate.isValid) {
          const updatedValues = updateValuesFromFields(values, fields)

          const monthlyPaymentRefinance = getMonthlyPayment(
            updatedValues.remainingBalance,
            updatedValues.newRate,
            updatedValues.numPayments
          )
          const monthlySavings = currency(updatedValues.monthlyPayment).subtract(monthlyPaymentRefinance)

          const numPayments =
            getNumPayments(updatedValues.remainingBalance, updatedValues.monthlyPayment, updatedValues.interestRate) - 1

          const amortization = getAmortization(
            updatedValues.remainingBalance,
            updatedValues.interestRate,
            numPayments,
            updatedValues.monthlyPayment
          )

          const amortizationRefinance = getAmortization(
            updatedValues.remainingBalance,
            updatedValues.newRate,
            updatedValues.numPayments,
            monthlyPaymentRefinance
          )

          dispatch({ type: 'setValues', payload: { ...updatedValues } })
          dispatch({ type: 'setValue', payload: { name: 'monthlySavings', value: monthlySavings } })
          dispatch({ type: 'setValue', payload: { name: 'amortization', value: amortization } })
          dispatch({ type: 'setValue', payload: { name: 'amortizationRefinance', value: amortizationRefinance } })
        }

        dispatch({ type: 'setFields', payload: validate.fields })

        return validate.isValid
      },
    },
    {
      name: 'Calculate',
      component: PanelCalculate,
    },
    {
      name: 'Chart',
      component: PanelChart,
    },
    {
      name: 'Detail',
      component: PanelDetail,
    },
  ],
  fields: [
    {
      type: 'header',
      text: 'Existing Loan',
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'remainingBalance',
      label: 'Remaining Balance on Your Current Loan',
      value: 100000,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'monthlyPayment',
      label: 'Current Monthly Payment',
      value: 900,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'interestRate',
      label: 'Current Rate',
      value: 8.9,
    },
    {
      type: 'header',
      text: 'New Loan',
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'newRate',
      label: 'New Rate',
      value: 6.9,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'points',
      label: 'Points',
      value: 0,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'feesCosts',
      label: 'Fees and Cloing Costs',
      value: 1000,
    },
    {
      type: 'number',
      dataType: 'int',
      name: 'numPayments',
      label: 'New Term (Months)',
      value: 360,
    },
    {
      type: 'header',
      text: 'Other Information',
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'taxRate',
      label: 'Your Income Tax Rate',
      value: 28,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'returnRate',
      label: 'Your Investment Rate of Return',
      value: 5,
    },
    {
      type: 'number',
      dataType: 'int',
      name: 'keepMonths',
      label: 'How Long Will You Keep This Property? (Months)',
      value: 0,
    },
  ],
}
